import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type {
  BankAccount,
  BankConnection,
  BankConnectionStatus,
  BankProvider,
  BankTransaction,
} from '../types';
import {
  ASSET_CATEGORY_OPTIONS,
  LIABILITY_CATEGORY_OPTIONS,
} from '../lib/assetCategories';
import { db } from '../lib/firebase';
import {
  COLLECTION_BANK_ACCOUNTS,
  COLLECTION_BANK_CONNECTIONS,
  COLLECTION_BANK_TRANSACTIONS,
  ownedItemDocId,
} from '../lib/firestorePaths';

const BANK_TRANSACTION_EXTRA_CATEGORY_OPTIONS = [
  { value: 'transfer', label: 'Transfer' },
  { value: 'fees', label: 'Fees' },
  { value: 'uncategorized', label: 'Uncategorized' },
] as const;

export type BankTransactionCategoryOption = {
  value: string;
  label: string;
};

export const BANK_TRANSACTION_CATEGORY_OPTIONS: BankTransactionCategoryOption[] = [
  ...ASSET_CATEGORY_OPTIONS,
  ...LIABILITY_CATEGORY_OPTIONS,
  ...BANK_TRANSACTION_EXTRA_CATEGORY_OPTIONS.map((option) => ({ ...option })),
];

const BANK_TRANSACTION_CATEGORY_LABELS = new Map(
  BANK_TRANSACTION_CATEGORY_OPTIONS.map((option) => [option.value, option.label])
);

export function bankTransactionCategoryLabel(category?: string): string {
  if (!category || category === 'uncategorized') return 'Uncategorized';
  return BANK_TRANSACTION_CATEGORY_LABELS.get(category) ?? category;
}

export async function updateBankTransactionCategory(
  userId: string,
  transactionId: string,
  category: string
): Promise<void> {
  const docRef = doc(
    db,
    COLLECTION_BANK_TRANSACTIONS,
    ownedItemDocId(userId, transactionId)
  );
  await updateDoc(docRef, { category });
}

export type BankWorkspaceState = {
  bankConnections: BankConnection[];
  bankAccounts: BankAccount[];
  bankTransactions: BankTransaction[];
  loading: boolean;
  error: string | null;
};

const EMPTY_STATE: BankWorkspaceState = {
  bankConnections: [],
  bankAccounts: [],
  bankTransactions: [],
  loading: false,
  error: null,
};

const CONNECTION_STATUSES: BankConnectionStatus[] = [
  'pending',
  'connected',
  'error',
  'disconnected',
];

function asBankProvider(value: unknown): BankProvider {
  return value === 'manual' ? 'manual' : 'qonto';
}

function asConnectionStatus(value: unknown): BankConnectionStatus {
  return CONNECTION_STATUSES.includes(value as BankConnectionStatus)
    ? (value as BankConnectionStatus)
    : 'pending';
}

function docItemId(d: QueryDocumentSnapshot, data: { id?: unknown }): string {
  if (typeof data.id === 'string' && data.id.trim()) return data.id.trim();
  const parts = d.id.split('__');
  return parts.length > 1 ? parts.slice(1).join('__') : d.id;
}

/** Strip backend-only token fields; client rules allow read but tokens must not enter app state. */
function docToBankConnection(d: QueryDocumentSnapshot): BankConnection {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  return {
    id: docItemId(d, x),
    userId: typeof x.userId === 'string' ? x.userId : '',
    provider: asBankProvider(x.provider),
    status: asConnectionStatus(x.status),
    organizationName:
      typeof x.organizationName === 'string' && x.organizationName.trim()
        ? x.organizationName.trim()
        : undefined,
    lastSyncAt: typeof x.lastSyncAt === 'string' ? x.lastSyncAt : undefined,
    lastSyncError:
      typeof x.lastSyncError === 'string' && x.lastSyncError.trim()
        ? x.lastSyncError.trim()
        : undefined,
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
    updatedAt: typeof x.updatedAt === 'string' ? x.updatedAt : now,
  };
}

function docToBankAccount(d: QueryDocumentSnapshot): BankAccount {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const balance = typeof x.balance === 'number' && Number.isFinite(x.balance) ? x.balance : 0;
  return {
    id: docItemId(d, x),
    userId: typeof x.userId === 'string' ? x.userId : '',
    connectionId: typeof x.connectionId === 'string' ? x.connectionId : '',
    provider: asBankProvider(x.provider),
    externalId: typeof x.externalId === 'string' ? x.externalId : '',
    ibanMasked: typeof x.ibanMasked === 'string' ? x.ibanMasked : '',
    name: typeof x.name === 'string' && x.name.trim() ? x.name.trim() : 'Account',
    websiteUrl:
      typeof x.websiteUrl === 'string' && x.websiteUrl.trim() ? x.websiteUrl.trim() : undefined,
    currency: 'EUR',
    balance,
    balanceUpdatedAt:
      typeof x.balanceUpdatedAt === 'string' ? x.balanceUpdatedAt : undefined,
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
    updatedAt: typeof x.updatedAt === 'string' ? x.updatedAt : now,
  };
}

function docToBankTransaction(d: QueryDocumentSnapshot): BankTransaction {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const amount = typeof x.amount === 'number' && Number.isFinite(x.amount) ? x.amount : 0;
  return {
    id: docItemId(d, x),
    userId: typeof x.userId === 'string' ? x.userId : '',
    bankAccountId: typeof x.bankAccountId === 'string' ? x.bankAccountId : '',
    externalId: typeof x.externalId === 'string' ? x.externalId : '',
    bookedAt: typeof x.bookedAt === 'string' ? x.bookedAt : now,
    amount,
    currency: 'EUR',
    counterpartyName:
      typeof x.counterpartyName === 'string' ? x.counterpartyName : '',
    label: typeof x.label === 'string' ? x.label : '',
    reference: typeof x.reference === 'string' ? x.reference : undefined,
    category: typeof x.category === 'string' ? x.category : undefined,
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
  };
}

export function useBankWorkspaceState(userId: string): BankWorkspaceState {
  const [bankConnections, setBankConnections] = useState<BankConnection[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [bankTransactions, setBankTransactions] = useState<BankTransaction[]>([]);
  const [loading, setLoading] = useState(Boolean(userId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setBankConnections([]);
      setBankAccounts([]);
      setBankTransactions([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    const unsubscribers: (() => void)[] = [];

    const qConnections = query(
      collection(db, COLLECTION_BANK_CONNECTIONS),
      where('userId', '==', userId)
    );
    const qAccounts = query(
      collection(db, COLLECTION_BANK_ACCOUNTS),
      where('userId', '==', userId)
    );
    const qTransactions = query(
      collection(db, COLLECTION_BANK_TRANSACTIONS),
      where('userId', '==', userId)
    );

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const [connectionsSnap, accountsSnap, transactionsSnap] = await Promise.all([
          getDocs(qConnections),
          getDocs(qAccounts),
          getDocs(qTransactions),
        ]);
        if (cancelled) return;

        setBankConnections(connectionsSnap.docs.map(docToBankConnection));
        setBankAccounts(accountsSnap.docs.map(docToBankAccount));
        setBankTransactions(transactionsSnap.docs.map(docToBankTransaction));

        unsubscribers.push(
          onSnapshot(
            qConnections,
            (snap) => setBankConnections(snap.docs.map(docToBankConnection)),
            (e) => setError(e.message)
          ),
          onSnapshot(
            qAccounts,
            (snap) => setBankAccounts(snap.docs.map(docToBankAccount)),
            (e) => setError(e.message)
          ),
          onSnapshot(
            qTransactions,
            (snap) => setBankTransactions(snap.docs.map(docToBankTransaction)),
            (e) => setError(e.message)
          )
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load bank data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [userId]);

  return useMemo(
    () => ({
      bankConnections,
      bankAccounts,
      bankTransactions,
      loading,
      error,
    }),
    [bankConnections, bankAccounts, bankTransactions, loading, error]
  );
}

export function emptyBankWorkspaceState(): BankWorkspaceState {
  return EMPTY_STATE;
}
